// 배당 자동 기록 — 앱을 열 때(로그인 사용자 본인 세션) 보유종목 × 공개 배당 일정으로 지급일이 지난 회차를 기록.
// 서버는 공개 정보(운용사·YieldMax 분배 표)만 제공하고 사용자 기록은 건드리지 않음 → 저장은 기존 saveDivs 경로 그대로.
//
// 대상: 국내 ETF(KODEX·TIGER·ACE·RISE·SOL·PLUS, 운용사 공시 지급일) + 미국 YieldMax ETF(공식 표 지급일)
// 규칙
//  - 입금일: 국내 ETF = 실지급일, 미국 = 미국 지급일 다음 영업일(금 → 월)
//  - 입금일이 오늘 이전이고, 최근 45일 안이며, 그 종목의 마지막 기록보다 뒤인 회차만
//  - 같은 종목 기록이 지급일 -2 ~ +7일 안에 이미 있으면 건너뜀 (직접 넣은 기록과 중복 방지)
//  - 과세표준이 아직 미발표면 일반 과세로 넣고 autoPending 표시 → 다음에 열 때 공시되면 다시 계산
import { computeDividendDerived } from "./dividends";

export const AUTO_LOOKBACK_DAYS = 45;
const KR_BRANDS = ["KODEX", "TIGER", "ACE", "RISE", "SOL", "PLUS"];

const norm = (s) => String(s || "").replace(/\s+/g, "").toLowerCase();
const dayMs = 86400000;
const toMs = (d) => Date.parse(d + "T00:00:00Z");
const toYmd = (ms) => new Date(ms).toISOString().slice(0, 10);
const has = (v) => v !== "" && v !== undefined && v !== null && Number.isFinite(parseFloat(v));

export function kstToday(now = Date.now()) {
  return new Date(now + 9 * 3600 * 1000).toISOString().slice(0, 10);
}
// 다음 영업일 (주말만 고려)
export function nextBusinessDay(ymd) {
  let t = toMs(ymd) + dayMs;
  while ([0, 6].includes(new Date(t).getUTCDay())) t += dayMs;
  return toYmd(t);
}
export function krBrandOf(name) {
  const n = String(name || "").trim().toUpperCase();
  return KR_BRANDS.find((b) => n.startsWith(b)) || null;
}
// 보유종목 → 자동 기록 소스 판별
export function sourceOf(h) {
  if (!h || !h.name) return null;
  if (h.market === "US") {
    const t = String(h.code || (/^[A-Za-z]{1,6}$/.test(h.name) ? h.name : "")).toUpperCase();
    return /^[A-Z]{1,6}$/.test(t) ? { kind: "us", ticker: t } : null;
  }
  if (krBrandOf(h.name)) return { kind: "kr-etf", code: String(h.code || "").toUpperCase() };
  return null;
}
// 이 보유종목에 해당하는 기존 배당 기록 (이름 또는 티커로 매칭)
export function recordsOf(h, dividends) {
  const keys = new Set([norm(h.name), norm(h.code)].filter(Boolean));
  return (dividends || []).filter((d) => keys.has(norm(d.name)));
}
// 기록에 쓸 종목명: 기존 기록에서 쓰던 이름 우선 (예: 보유종목은 'YieldMax COIN ...' 이어도 기록은 'CONY')
export function recordNameOf(h, recs) {
  const latest = [...recs].sort((a, b) => String(b.date).localeCompare(String(a.date)))[0];
  if (latest && latest.name) return latest.name;
  return h.market === "US" && h.code ? String(h.code).toUpperCase() : h.name;
}

/**
 * 자동 기록 계획 세우기 (저장은 호출한 쪽에서)
 * @param {object} p
 * @param {Array} p.holdings  배당 보유종목
 * @param {Array} p.dividends 현재 배당 기록
 * @param {string} p.today    KST YYYY-MM-DD
 * @param {object} p.fetch    { usDist(ticker) → {supported, items[{payDate, amount, roc}]},
 *                              krEtf(code, name) → {supported, items[{payDate, amount, taxBase}]},
 *                              rate(date) → number|null, krCode(name) → code|null }
 * @returns {Promise<{ add: Array, update: Array, log: Array }>}
 */
export async function planAutoDividends({ holdings, dividends, today, fetch: f, idBase = Date.now() }) {
  const add = [], update = [], log = [];
  const cutoff = toMs(today) - AUTO_LOOKBACK_DAYS * dayMs;
  let seq = 0;

  for (const h of holdings || []) {
    const src = sourceOf(h);
    const shares = parseFloat(h.shares);
    if (!src || !(shares > 0)) continue;
    const recs = recordsOf(h, dividends);
    const exempt = !!h.exempt || h.taxType === "exempt";

    let items = [];
    try {
      if (src.kind === "us") {
        const r = await f.usDist(src.ticker);
        if (!r || !r.supported) { log.push(`${h.name}: 자동 조회 미지원 종목`); continue; }
        items = r.items || [];
      } else {
        const code = /^[0-9A-Z]{6}$/.test(src.code) ? src.code : await f.krCode(h.name);
        if (!code) { log.push(`${h.name}: 종목코드 없음`); continue; }
        const r = await f.krEtf(code, h.name);
        if (!r || !r.supported || r.notFound) { log.push(`${h.name}: 운용사 분배 내역 없음`); continue; }
        items = r.items || [];
      }
    } catch (e) {
      log.push(`${h.name}: 조회 실패 (${e.message})`);
      continue;
    }

    const lastDate = recs.reduce((m, d) => (String(d.date) > m ? String(d.date) : m), "");
    const name = recordNameOf(h, recs);
    const currency = src.kind === "us" ? "USD" : "KRW";

    for (const it of items) {
      if (!it || !it.payDate || !(it.amount > 0)) continue;
      const receipt = src.kind === "us" ? nextBusinessDay(it.payDate) : it.payDate;
      const rMs = toMs(receipt);
      if (rMs > toMs(today) || rMs < cutoff) continue;          // 아직 안 들어왔거나 너무 오래됨
      if (lastDate && receipt <= lastDate) continue;              // 마지막 기록 이전 회차는 건드리지 않음
      const pMs = toMs(it.payDate);
      const dup = recs.some((d) => { const t = toMs(String(d.date)); return t >= pMs - 2 * dayMs && t <= pMs + 7 * dayMs; })
        || add.some((d) => norm(d.name) === norm(name) && d.autoPayDate === it.payDate);
      if (dup) continue;

      const amount = currency === "USD" ? +(it.amount * shares).toFixed(2) : Math.round(it.amount * shares);
      const taxBase = !exempt && currency === "KRW" && it.taxBase != null ? String(it.taxBase) : "";
      const pending = !exempt && currency === "KRW" && it.taxBase == null;
      const derived = computeDividendDerived(
        { amount, afterTax: "", shares, currency, taxExempt: exempt, taxBasePerShare: taxBase, taxPerShare: "" }, {});
      let rate = "";
      if (currency === "USD") { const r = await f.rate(receipt); if (r) rate = +Number(r).toFixed(2); }
      add.push({
        id: `${idBase}${String(seq++).padStart(2, "0")}`,
        date: receipt, broker: h.broker || "", name, currency,
        shares: String(shares), amount: String(amount),
        tax: derived.tax !== undefined ? derived.tax : "", afterTax: derived.afterTax !== undefined ? derived.afterTax : "",
        perShare: derived.perShare !== undefined ? derived.perShare : +(it.amount).toFixed(4),
        rate, taxExempt: exempt, taxBasePerShare: taxBase, taxPerShare: "",
        memo: src.kind === "us" ? `자동 기록 · 미국 지급일 ${it.payDate}` : `자동 기록 · ${it.payDate} 지급${pending ? " (과세표준 미발표)" : ""}`,
        auto: true, autoPayDate: it.payDate, autoSrc: src.kind === "us" ? "yieldmax" : krBrandOf(h.name), ...(pending ? { autoPending: true } : {}),
      });
    }

    // 과세표준 미발표로 넣어둔 자동 기록 → 이제 공시됐으면 다시 계산 (사용자가 고친 기록은 제외)
    if (src.kind === "kr-etf") {
      for (const d of recs) {
        if (!d.auto || !d.autoPending || d.autoEdited || d.taxExempt) continue;
        const it = items.find((x) => x.payDate === d.autoPayDate);
        if (!it || it.taxBase == null) continue;
        const derived = computeDividendDerived(
          { amount: d.amount, afterTax: "", shares: d.shares, currency: "KRW", taxExempt: false, taxBasePerShare: String(it.taxBase), taxPerShare: "" }, {});
        const { autoPending, ...rest } = d;
        update.push({ ...rest, taxBasePerShare: String(it.taxBase), tax: derived.tax, afterTax: derived.afterTax, memo: `자동 기록 · ${it.payDate} 지급` });
      }
    }
  }
  return { add, update, log };
}

export function applyAutoPlan(dividends, { add, update }) {
  const upd = new Map(update.map((d) => [d.id, d]));
  const next = (dividends || []).map((d) => upd.get(d.id) || d).concat(add);
  return next.sort((a, b) => String(b.date).localeCompare(String(a.date)));
}
